<template>
    <div class="tw-my-8">
        <form
            class="tw-p-0"
            @submit.prevent="validateBeforeSubmit"
        >
            <div class="tw-grid md:tw-grid-cols-2">
                <div class="tw-flex tw-flex-col md:tw-mr-5">
                    <TextInputComponent
                        :label="$t('personalInformation.firstName')"
                        name="firstName"
                        :value="firstName"
                        @change="updateField('firstName', $event.target.value)"
                    />
                    <span
                        class="tw-text-[var(--brand-primary)]"
                        v-if="$v.firstName.$dirty && !$v.firstName.required"
                    >
                        {{ $t("errorMessages.missingFirstName") }}
                    </span>
                </div>
                <div class="tw-flex tw-flex-col">
                    <TextInputComponent
                        :label="$t('personalInformation.lastName')"
                        name="lastName"
                        :value="lastName"
                        @change="updateField('lastName', $event.target.value)"
                    />
                    <span
                        class="tw-text-[var(--brand-primary)]"
                        v-if="$v.lastName.$dirty && !$v.lastName.required"
                    >
                        {{ $t("errorMessages.missingLastName") }}
                    </span>
                </div>
            </div>
            <div class="tw-flex tw-flex-col">
                <TextInputComponent
                    :label="$t('personalInformation.email')"
                    name="email"
                    type="email"
                    :value="email"
                    @change="updateField('email', $event.target.value)"
                    @focusout="onEmailBlur"
                />
                <span
                    class="tw-text-[var(--brand-primary)]"
                    v-if="emailBlurred && !$v.email.required"
                >
                    {{ $t("errorMessages.missingEmail") }}
                </span>
                <span
                    class="tw-text-[var(--brand-primary)]"
                    v-if="emailBlurred && !$v.email.email"
                >
                    {{ $t("errorMessages.incorrectEmail") }}
                </span>
                <span
                    class="tw-text-[var(--brand-primary)]"
                    v-if="emailBlurred && $v.email.email && domainCheckPerformed && !domainIsValid && email.includes('@')"
                >
                    {{ $t("errorMessages.invalidEmailDomain") }}
                </span>
            </div>
            <div v-if="inquiryType === 'phone-inquiry'" class="tw-flex tw-flex-col">
                <PhoneInputComponent
                    :label="$t('personalInformation.phone')"
                    name="phone"
                    :country="phoneCountry"
                    :value="phone"
                    @change="updateField('phone', $event)"
                    @countryChange="onCountryChange"
                    @focusout="onPhoneBlur"
                />
                <span
                    class="tw-text-[var(--brand-primary)]"
                    v-if="phoneBlurred && !$v.phone.required"
                >
                    {{ $t("errorMessages.missingPhone") }}
                </span>
                <span
                    class="tw-text-[var(--brand-primary)]"
                    v-if="phoneBlurred && $v.phone.required && !$v.phone.onlyNumbers"
                >
                    {{ $t("errorMessages.invalidPhone") }}
                </span>
                <span
                    class="tw-text-[var(--brand-primary)]"
                    v-if="phoneBlurred && $v.phone.onlyNumbers && !$v.phone.minLength"
                >
                    {{ $t("errorMessages.phoneTooShort") }}
                </span>
            </div>
            <div v-if="!isAsiInquiry" class="tw-flex tw-items-baseline tw-gap-2 tw-my-4">
                <input
                    class="tw-accent-[var(--brand-primary)]"
                    type="checkbox"
                    name="mailchimpSignup"
                    id="mailchimpSignup"
                    :value="mailchimpSignup"
                    @change="updateField('mailchimpSignup', $event?.target?.checked)"
                />
                <label
                    class="tw-w-full tw-block"
                    for="mailchimpSignup"
                    v-html="$t('personalInformation.mailchimpSignup')"
                />
            </div>
            <div class="tw-flex tw-flex-col">
                <div class="tw-flex tw-items-baseline tw-gap-2 tw-mt-4">
                    <input
                        class="tw-accent-[var(--brand-primary)]"
                        type="checkbox"
                        name="tos"
                        id="tos"
                        :value="acceptToS"
                        @change="updateField('acceptToS', $event?.target?.checked)"
                    />
                    <label
                        class="tw-w-full tw-block"
                        for="tos"
                        v-html="$t('personalInformation.agbConfirmation')"
                    />
                </div>
                <span
                    class="tw-text-[var(--brand-primary)] tw-block tw-mt-3"
                    v-if="$v.acceptToS.$dirty && !$v.acceptToS.required"
                >
                    {{ $t("errorMessages.tosNotAccepted") }}
                </span>
            </div>
        </form>
    </div>
</template>

<script>
import TextInputComponent from "../UI/TextInputComponent.vue"
import PhoneInputComponent from "../UI/PhoneInputComponent.vue"
import {validationMixin} from "vuelidate";
import {email, required} from "vuelidate/lib/validators";
import axios from "axios";
import { mapGetters } from "vuex";

const onlyNumbers = (value) => /^\d+$/.test(value);
const minLength = (min) => (value) => value != null && value.length >= min;

export default {
    name: "CTAFields",
    components: {TextInputComponent, PhoneInputComponent},
    mixins: [validationMixin],
    props: {
        inquiryType: {
            type: String,
        }
    },
    data: () => ({
        loading: false,
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        acceptToS: false,
        mailchimpSignup: false,
        domainIsValid: false,
        phoneValid: false,
        domainCheckPerformed: false,
        emailBlurred: false,
        phoneBlurred: false,
    }),
    computed: {
        ...mapGetters({
            phoneCountry: 'tour/phoneCountry',
            isAsiInquiry: 'tour/isAsiInquiry',
        }),
    },
    validations: {
        firstName: {
            required
        },
        lastName: {
            required
        },
        email: {
            required,
            email
        },
        phone: {
            required,
            onlyNumbers,
            minLength: minLength(10)
        },
        acceptToS: {
            required
        },
        mailchimpSignup: {
        }
    },
    methods: {
        onEmailBlur() {
            // Mark email as blurred for validation display
            this.emailBlurred = true;
            // Touch the field for validation
            this.$v.email.$touch();
            // Only check domain if email has a valid format and contains @
            if (this.email && this.email.includes('@') && this.$v.email.email) {
                this.checkEmailDomain();
            }
        },
        onPhoneBlur() {
            // Mark phone as blurred for validation display
            this.phoneBlurred = true;
            // Touch the field for validation
            this.$v.phone.$touch();
        },
        checkEmailDomain() {
            const email = this.email;
            const domain = email.substring(email.lastIndexOf("@") + 1);

            axios.get(`/api/check-domain?domain=${domain}`)
                .then((response) => {
                    this.domainIsValid = !!response.data.exists;
                    this.domainCheckPerformed = true;
                    // Update store with email validity based only on format validation
                    const emailIsValid = !this.$v.email.$invalid;
                    this.$store.commit('tour/setEmailValid', emailIsValid)
                })
                .catch(error => {
                    console.error('Error:', error);
                    this.domainIsValid = false;
                    this.domainCheckPerformed = true;
                    // Update store with email validity based only on format validation
                    const emailIsValid = !this.$v.email.$invalid
                    this.$store.commit('tour/setEmailValid', emailIsValid);
                })
                .finally(() => {
                    this.$v.email.$touch();
                });
        },
        validateBeforeSubmit() {
            this.$v.$touch()
            // Mark email and phone as blurred for validation display on form submit
            this.emailBlurred = true;
            this.phoneBlurred = true;
            if (!this.$v.$invalid) {
                this.$emit("selected")
            }
        },
        updateField(fieldName, value) {
            this.$v[fieldName].$touch()
            this[fieldName] = value
            switch (fieldName) {
                case 'firstName':
                    this.$store.commit('tour/setFirstName', value)
                    break;
                case 'lastName':
                    this.$store.commit('tour/setLastName', value)
                    break;
                case 'email': {
                    this.$store.commit('tour/setEmail', value)
                    // Reset validation states when email changes
                    this.domainCheckPerformed = false;
                    this.domainIsValid = false;
                    this.emailBlurred = false;
                    // Email is valid if it passes basic format validation only
                    const emailIsValid = !this.$v.email.$invalid;
                    this.$store.commit('tour/setEmailValid', emailIsValid)
                    break;
                }
                case 'phone':
                    this.$store.commit('tour/setPhone', value)
                    // Reset validation state when phone changes
                    this.phoneBlurred = false;
                    this.phoneValid = !this.$v.phone.$invalid
                    this.$store.commit('tour/setPhoneValid', this.phoneValid)
                    this.$v.phone.$touch()
                    break;
                case 'acceptToS':
                    this.$store.commit('tour/setToS', value)
                    break;
                case 'mailchimpSignup':
                    this.$store.commit('tour/setMailchimpSignup', value)
                    break;
            }
        },
        // As this is part of the travelData now which gets persisted in
        // localStorage, we need to update the state if the user changes the
        // country for the phone number.
        onCountryChange(countryCode) {
            this.$store.commit('tour/setPhoneCountry', countryCode)
        }
    },
}
</script>